import React from 'react';
import ReactMarkdown from 'react-markdown';
import { AimieRobotAvatarIcon, UserAvatarIcon } from '../ChatAvatarIcons';
import ModeSelector from '../ModeSelector';
import StimulusAnnotationText from './StimulusAnnotationText';

const preserveSingleLineBreaks = (text) => (text || '').replace(/\n/g, '  \n');

const MarkdownMessage = ({ text, className = '' }) => (
  <div className={className}>
    <ReactMarkdown
      components={{
        p: ({ children }) => <>{children}</>,
      }}
    >
      {preserveSingleLineBreaks(text)}
    </ReactMarkdown>
  </div>
);

const RANDOM_SKILL_OPTIONS = [
  { value: 'OQ', label: 'Open-Ended Question' },
  { value: 'AF', label: 'Affirmation' },
  { value: 'SR', label: 'Simple Reflection' },
  { value: 'CR', label: 'Complex Reflection' },
  { value: 'S', label: 'Summary' },
];

function OarsPracticePane({
  t,
  theme = 'dark',
  activeMode,
  onFreeForm,
  onBeTheClient,
  onOars,
  onStrategies,
  showInlineModeSelector = false,
  hideEmptyStateTitle = false,
  hideEmptyStateDescription = false,
  singleColumnEmptyPrompt = '',
  oarsExercise,
  oarsExerciseLoading = false,
  replayUtterance = null,
  stimulusAnnotations = [],
  oarsPopupBlocking,
  oarsPendingUtterance,
  showRandomSkillChoicePanel = false,
  selectedRandomSkillType = null,
  onSelectRandomSkillType,
  showRandomSkillChoiceCue = false,
  suppressAnnotationTooltips = false,
  userAvatarPreference,
  messagesContainerRef,
  messagesEndRef,
}) {
  if (oarsPopupBlocking) {
    return (
      <div className="chat-empty-state entrance">
        <div className="chat-empty-copy">
          {!hideEmptyStateTitle && <h2>{t('mode_oars')}</h2>}
          {!hideEmptyStateDescription && <p>{t('welcome_oars')}</p>}
          {singleColumnEmptyPrompt && (
            <p className="chat-empty-single-column-prompt">{singleColumnEmptyPrompt}</p>
          )}
        </div>
        {showInlineModeSelector && (
          <div className="chat-empty-mode-selector-shell">
            <ModeSelector
              modeBarVisible
              setModeBarVisible={() => {}}
              activeMode={activeMode}
              onFreeForm={onFreeForm}
              onBeTheClient={onBeTheClient}
              onOars={onOars}
              onStrategies={onStrategies}
              variant="inline"
            />
          </div>
        )}
      </div>
    );
  }

  if (oarsExerciseLoading) {
    return (
      <div className="messages-container oars-messages oars-messages--loading" ref={messagesContainerRef} aria-live="polite">
        <div className="oars-session-position-indicator oars-session-position-indicator--loading">
          <span className="oars-session-position-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="oars-session-position-text">Fetching next exercise</span>
        </div>

        <div className="message counsellor oars-loading-message">
          <div className="oars-loading-bubble oars-loading-bubble--wide">
            <span className="oars-loading-line oars-loading-line--long" />
            <span className="oars-loading-line oars-loading-line--mid" />
          </div>
        </div>

        <div className="message client oars-loading-message">
          <div className="oars-loading-bubble oars-loading-bubble--client">
            <span className="oars-loading-line oars-loading-line--mid" />
            <span className="oars-loading-line oars-loading-line--short" />
          </div>
        </div>

        <div className="message counsellor oars-loading-message">
          <div className="oars-loading-bubble oars-loading-bubble--wide">
            <span className="oars-loading-line oars-loading-line--long" />
            <span className="oars-loading-line oars-loading-line--mid" />
            <span className="oars-loading-line oars-loading-line--short" />
          </div>
        </div>

        <div className="oars-cut-indicator oars-cut-indicator--loading">
          <span className="oars-cut-line" />
          <span className="oars-cut-label">Preparing your next turn...</span>
          <span className="oars-cut-line" />
        </div>

        <div ref={messagesEndRef} />
      </div>
    );
  }

  if (!oarsExercise) {
    return (
      <div className="chat-empty-state entrance">
        <div className="chat-empty-copy">
          {!hideEmptyStateTitle && <h2>{t('mode_oars')}</h2>}
          {!hideEmptyStateDescription && <p>{t('welcome_oars')}</p>}
          {singleColumnEmptyPrompt && (
            <p className="chat-empty-single-column-prompt">{singleColumnEmptyPrompt}</p>
          )}
        </div>
        {showInlineModeSelector && (
          <div className="chat-empty-mode-selector-shell">
            <ModeSelector
              modeBarVisible
              setModeBarVisible={() => {}}
              activeMode={activeMode}
              onFreeForm={onFreeForm}
              onBeTheClient={onBeTheClient}
              onOars={onOars}
              onStrategies={onStrategies}
              variant="inline"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="messages-container oars-messages" ref={messagesContainerRef}>
      <>
        <div className="oars-session-position-indicator">
          {oarsExercise.is_mid_conversation && (
            <span className="oars-session-position-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          )}
          <span className="oars-session-position-text">
            {oarsExercise.is_mid_conversation
              ? 'You are in the middle of a session'
              : 'You are at the beginning of a session'}
          </span>
        </div>
        {oarsExercise.conversation_history?.slice(-6).map((turn, idx) => {
          const turnIndex = Math.max(0, oarsExercise.conversation_history.length - 6) + idx;
          const turnAnnotations = stimulusAnnotations.filter(
            (annotation) => annotation.turn_id === `turn_${turnIndex}`
          );
          return (
          <div key={turnIndex} className={`message ${turn.speaker}`}>
            {turn.speaker === 'client' && (
              <span className="message-avatar message-avatar--robot" aria-hidden="true">
                <AimieRobotAvatarIcon />
              </span>
            )}
            <div className="message-bubble-shell">
              {turn.speaker === 'counsellor' ? (
                <MarkdownMessage className="message-text message-text--markdown" text={turn.text} />
              ) : (
                <StimulusAnnotationText
                  text={turn.text}
                  annotations={turnAnnotations}
                  theme={theme}
                  t={t}
                  suppressed={suppressAnnotationTooltips}
                />
              )}
            </div>
            {turn.speaker === 'counsellor' && (
              <span className="message-avatar message-avatar--human" aria-hidden="true">
                <UserAvatarIcon {...userAvatarPreference} />
              </span>
            )}
          </div>
          );
        })}
        <div className="oars-cut-indicator">
          <span className="oars-cut-line" />
          <span className="oars-cut-label">Your turn: what would you say next?</span>
          <span className="oars-cut-line" />
        </div>
        {showRandomSkillChoicePanel && !selectedRandomSkillType && (
          <div
            className={`oars-skill-choice-panel${showRandomSkillChoiceCue ? ' oars-skill-choice-panel--selection-cue' : ''}`}
            data-guided-highlight-purpose={showRandomSkillChoiceCue ? 'Choose the MI skill you think fits this moment.' : undefined}
          >
            <div className="oars-skill-choice-panel__header">
              <span className="oars-skill-choice-panel__label">Which Skill will you use</span>
            </div>
            <div className="oars-skill-choice-panel__actions" role="group" aria-label="Skill choice">
              {RANDOM_SKILL_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`oars-skill-choice-btn${selectedRandomSkillType === option.value ? ' selected' : ''}`}
                  onClick={() => onSelectRandomSkillType?.(option.value)}
                  aria-label={option.label}
                  title={option.label}
                >
                  {option.value}
                </button>
              ))}
            </div>
          </div>
        )}
        {(oarsPendingUtterance || replayUtterance) && (
          <div className="message counsellor">
            <div className="message-bubble-shell">
              <div className="message-text message-text--user">{oarsPendingUtterance || replayUtterance}</div>
            </div>
            <span className="message-avatar message-avatar--human" aria-hidden="true">
              <UserAvatarIcon {...userAvatarPreference} />
            </span>
          </div>
        )}
      </>
      <div ref={messagesEndRef} />
    </div>
  );
}

export default OarsPracticePane;
